// MarkdownRenderer.tsx
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'hast-util-sanitize';
import { Box, Image, useColorModeValue } from '@chakra-ui/react';
import { ReactSVG } from 'react-svg'; // Ensure you have react-svg installed
import 'highlight.js/styles/stackoverflow-dark.css';
import { Base64 } from 'js-base64';

// Custom remark plugin to replace inline <svg> with a custom component
import { visit } from 'unist-util-visit';
import { Node } from 'unist'; // Import Base64 from js-base64

// Utility function to create a customizable sanitization schema without SVG
const createSanitizeSchema = (
    additionalTags: string[] = [],
    additionalAttributes: Record<string, string[]> = {}
) => {
    const schemaAttributes = defaultSchema.attributes || {};

    return {
        ...defaultSchema,
        tagNames: [...(defaultSchema.tagNames || []), ...additionalTags],
        attributes: {
            ...schemaAttributes,
            ...additionalAttributes,
            '*': ['className', 'style', ...(schemaAttributes['*'] || [])],
        },
    };
};

const remarkSvg = () => (tree: Node) => {
    visit(tree, 'html', (node: any, index, parent) => {
        if (node.value.trim().startsWith('<svg')) {
            // @ts-ignore
            parent.children[index] = {
                type: 'jsx',
                value: node.value,
            };
        }
    });
};

// Custom renderers for Markdown elements
const createMarkdownComponents = (codeBg: string, inlineCodeBg: string) => ({
    code({ node, inline, className, children, ...props }: any) {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';

        if (!inline && language === 'svg') {
            const svgContent = String(children).trim();
            let base64Svg = '';

            try {
                base64Svg = Base64.encode(svgContent);
            } catch (error) {
                console.error('Failed to encode SVG content:', error);
                return <Box color="red.500">Invalid SVG Content</Box>;
            }

            if (!base64Svg) {
                return <Box color="red.500">Invalid SVG Content</Box>;
            }

            return (
                <Box my={4}>
                    <ReactSVG
                        src={`data:image/svg+xml;base64,${base64Svg}`}
                        beforeInjection={(svg) => {
                            svg.setAttribute('style', 'width: 100%; height: auto;');
                        }}
                        fallback={() => <span>Failed to load SVG</span>}
                        loading={() => <span>Loading SVG...</span>}
                    />
                </Box>
            );
        }

        return !inline ? (
            <Box as="pre" bg={codeBg} p={2} borderRadius="md" overflowX="auto">
                <code className={className} {...props}>
                    {children}
                </code>
            </Box>
        ) : (
            <Box as="code" bg={inlineCodeBg} px={1} py={0.5} borderRadius="md" {...props}>
                {children}
            </Box>
        );
    },
    img({ src, alt, ...props }: any) {
        return <Image src={src || ''} alt={alt || ''} maxW="100%" borderRadius="md" {...props} />;
    },
    // Handle JSX nodes (for inline <svg>)
    jsx({ value }: any) {
        // Extract the SVG content
        const svgMatch = value.match(/<svg[^>]*>([\s\S]*?)<\/svg>/);
        if (svgMatch) {
            const svgContent = svgMatch[0];
            let base64Svg = '';

            try {
                base64Svg = Base64.encode(svgContent);
            } catch (error) {
                console.error('Failed to encode inline SVG content:', error);
                return <Box color="red.500">Invalid SVG Content</Box>;
            }

            if (!base64Svg) {
                return <Box color="red.500">Invalid SVG Content</Box>;
            }

            return (
                <Box my={4}>
                    <ReactSVG
                        src={`data:image/svg+xml;base64,${base64Svg}`}
                        beforeInjection={(svg) => {
                            svg.setAttribute('style', 'width: 100%; height: auto;');
                        }}
                        fallback={() => <span>Failed to load SVG</span>}
                        loading={() => <span>Loading SVG...</span>}
                    />
                </Box>
            );
        }
        return null;
    },
    // Add more custom renderers as needed
});

interface MarkdownRendererProps {
    content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
    // Color mode values for code blocks
    const codeBg = useColorModeValue('gray.100', 'gray.700');
    const inlineCodeBg = useColorModeValue('gray.100', 'gray.600');
    
    // Extend the sanitization schema without SVG
    const sanitizeSchema = createSanitizeSchema();
    
    // Create components with theme-aware colors
    const markdownComponents = createMarkdownComponents(codeBg, inlineCodeBg);

    return (
        <ReactMarkdown
            children={content}
            remarkPlugins={[remarkGfm, remarkSvg]}
            rehypePlugins={[
                rehypeRaw, // Allow raw HTML
                [rehypeSanitize, sanitizeSchema],
                rehypeHighlight,
            ]}
            components={markdownComponents}
        />
    );
};

export default MarkdownRenderer;
